import { DotsVerticalIcon } from "@heroicons/react/outline";
import { ReactNode } from "react";

import { CardVariant } from "./constants";
import { LoadingState } from "./types";

import {
  OverflowDirection,
  OverflowDisplayTooltip,
} from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { ProgressRadial } from "@/components/ui/ProgressRadial";
import { Action, DropdownMenu } from "@/components/ui/DropdownMenu";

interface CardHeaderProps<DataT> {
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  actions?: Action<DataT>[];
  dropdownMenuItems: Action<DataT>[];
  variant?: CardVariant;
  item?: DataT;
  loadingState?: LoadingState;
  singleLine?: boolean;
  disabled?: boolean;
  headerActions?: ReactNode;
}

export const CardHeader = <DataT,>({
  icon,
  title,
  subtitle,
  actions,
  variant,
  dropdownMenuItems,
  item,
  loadingState,
  singleLine = false,
  disabled = false,
  headerActions,
}: CardHeaderProps<DataT>) => {
  const hasLoadingState = loadingState?.progress;
  const isSelected = variant === CardVariant.SELECTED;
  const showTrailing = !disabled && (Boolean(headerActions) || Boolean(actions));
  const titleColorClassName = isSelected ? "text-gray-900" : "group-hover:text-blue-500";

  return (
    <div className="flex items-center justify-between">
      <div className="flex min-w-0 flex-1 items-center gap-x-2">
        {icon && (
          <div
            className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg p-1 transition-all duration-200 ease-in-out ${"bg-gray-50 group-hover:bg-gray-100"} ${variant === CardVariant.WARNING ? "!bg-amber-50" : ""}`}
          >
            {icon}
            {hasLoadingState ? (
              <ProgressRadial
                progress={loadingState?.progress}
                className="z-1 absolute inset-0 rounded-full bg-white bg-opacity-75"
              />
            ) : null}
          </div>
        )}
        {title && (
          <h3
            className={`flex min-w-0 flex-1 ${
              singleLine ? "text-sm" : "flex-col text-xs"
            } pr-2 font-medium transition-colors duration-200 ease-in-out ${titleColorClassName}`}
            title={title}
          >
            <OverflowDisplayTooltip
              content={title}
              className={`${singleLine ? "min-w-0 truncate" : "break-all"}`}
              overflowDirection={
                singleLine ? OverflowDirection.HORIZONTAL : OverflowDirection.VERTICAL
              }
              lineClamp={singleLine ? 1 : 2}
            >
              {title}
            </OverflowDisplayTooltip>
            {subtitle && (
              <span
                className={`${
                  singleLine ? "ml-2 flex-shrink-0" : "mt-1"
                } text-sm text-gray-400 transition-colors duration-200 ease-in-out`}
              >
                {subtitle}
              </span>
            )}
          </h3>
        )}
      </div>
      {showTrailing && (
        <div className="flex flex-shrink-0 items-center gap-0.5">
          {headerActions}
          {actions ? (
            <DropdownMenu<DataT>
              menuItems={dropdownMenuItems}
              item={item}
              triggerClassName={isSelected ? "bg-gray-100 hover:bg-gray-100" : ""}
              icon={<DotsVerticalIcon className="h-5 w-5" />}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};
